import { useChatActions, useChatMessages } from '@ai-sdk-tools/store';
import { useCallback, useRef } from 'react';

import type { ToolHandoffName } from '../../../lib/tool-handoff';
import type { UIMessageWithMetadata } from '../../../lib/types';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import type { RetryLastChatRequest } from '../lib/shared-types';
import { beginToolHandoffContinuation } from '../lib/tool-handoff-continuation';

interface SubmitToolHandoffContinuationOptions {
  tool: ToolHandoffName;
  toolCallId: string;
  output: unknown;
  retry?: RetryLastChatRequest;
  onSuccess?: () => void;
}

export function useToolHandoffContinuation() {
  const messages = useChatMessages<UIMessageWithMetadata>();
  const { addToolResult, sendMessage, setMessages } = useChatActions<UIMessageWithMetadata>();
  const { chatUuid, setRetryLastRequest } = useChatOverlayContext();
  const submissionInFlightRef = useRef(false);

  const submitToolHandoffContinuation = useCallback(
    async (options: SubmitToolHandoffContinuationOptions): Promise<boolean> => {
      if (submissionInFlightRef.current) {
        return false;
      }

      submissionInFlightRef.current = true;
      const pendingMessages = messages;
      let cancelSettlement: (() => void) | undefined;

      try {
        await addToolResult({
          tool: options.tool,
          toolCallId: options.toolCallId,
          output: options.output,
        });

        const settlement = beginToolHandoffContinuation(chatUuid);
        cancelSettlement = settlement.cancel;
        setRetryLastRequest(
          options.retry ??
            (async () => {
              await submitToolHandoffContinuation(options);
            }),
        );

        const [, outcome] = await Promise.all([sendMessage(undefined), settlement.outcome]);
        if (outcome === 'succeeded') {
          options.onSuccess?.();
          return true;
        }

        setMessages(pendingMessages);
        return false;
      } catch {
        cancelSettlement?.();
        setMessages(pendingMessages);
        return false;
      } finally {
        submissionInFlightRef.current = false;
      }
    },
    [addToolResult, chatUuid, messages, sendMessage, setMessages, setRetryLastRequest],
  );

  return { submitToolHandoffContinuation };
}
